import { createSelector } from 'reselect'
import { getState } from 'lib/store'
import { calculateEffectiveDateRange } from 'lib/search/calculate-effective-date-range'
import { isDemoMode } from 'lib/is-demo'
import type { State } from 'reducers'
import { TimeRange } from 'types/search/filters'
import type {
  DateFilter,
  AttributeValueFilter,
  CommonQueryFilters,
} from 'types/search/filters'
import { getManualSearchAt } from 'selectors/search/ui'

// Default values (reused to avoid creating new objects in selectors)
const EMPTY_DATE_FILTER: DateFilter = {
  startDate: null,
  endDate: null,
}
const EMPTY_ARRAY: AttributeValueFilter[] = []

// Reselect versions (accept state as parameter)
const getTimeRangeRS = (state: State): TimeRange => {
  return state.search?.filters?.timeRange || TimeRange.TEN_MIN
}

const getCustomDateRangeRS = (state: State): DateFilter => {
  return state.search?.filters?.customDateRange || EMPTY_DATE_FILTER
}

const getAttributeValueFiltersRS = (state: State): AttributeValueFilter[] => {
  return state.search?.filters?.attributeValues || EMPTY_ARRAY
}

// Public API (use global state)
export const getTimeRange = (): TimeRange => {
  return getState().search?.filters?.timeRange || TimeRange.TEN_MIN
}

export const getCustomDateRange = (): DateFilter => {
  return getState().search?.filters?.customDateRange || EMPTY_DATE_FILTER
}

export const getAttributeValueFilters = (): AttributeValueFilter[] => {
  return getState().search?.filters?.attributeValues || EMPTY_ARRAY
}

/**
 * Memoized selector that returns all common query filters (reselect version)
 * This includes:
 * - Effective date range (calculated from timeRange or customDateRange)
 * - Attribute value filters in GraphQL format
 *
 * Used by all data fetching actions (chart, logs, attribute values)
 * to ensure consistent filtering across all queries
 *
 * Note: This is the reselect version that accepts state as parameter.
 * Use getCommonQueryFilters() for the wrapper that uses global state.
 */
export const getCommonQueryFiltersRS = createSelector(
  [
    getTimeRangeRS,
    getCustomDateRangeRS,
    getAttributeValueFiltersRS,
    // depends on these to update common filters
    getManualSearchAt,
  ],
  (timeRange, customDateRange, attributeValues): CommonQueryFilters => {
    // Calculate effective date range
    const effectiveDateRange = calculateEffectiveDateRange(
      timeRange,
      customDateRange.startDate,
      customDateRange.endDate,
    )

    // Convert to GraphQL format
    return {
      dateRange: {
        from: effectiveDateRange.startDate,
        to: effectiveDateRange.endDate,
      },
      attributeValues: attributeValues.map((av) => ({
        attributeCode: av.attributeCode,
        attributeValue: av.value,
      })),
    }
  },
)

/**
 * Wrapper that uses global state - this is the public API used by actions
 */
export const getCommonQueryFilters = (): CommonQueryFilters => {
  return getCommonQueryFiltersRS(getState())
}

/**
 * Whether demo mode loaders should stay active (no custom period, only browserSession filter)
 * Used by fetch actions to keep loaders on when no data is returned
 */
export const isDemoLoading = (): boolean => {
  if (!isDemoMode) return false
  const timeRange = getTimeRange()
  const filters = getAttributeValueFilters()
  return (
    timeRange !== TimeRange.CUSTOM &&
    filters.length === 1 &&
    filters[0].attributeCode === 'params.browserSessionId'
  )
}
